import { Component, Prop, Vue } from 'vue-property-decorator';
import { POSITIONS } from '@/consts';

const BODY_ZOOM = 0.6;

type styleObject = {
  width: string | null
  top: string | null
  left: string | null
  right: string | null
  bottom: string | null
  position: string
};

@Component({})
export default class AbstractDropdownContent extends Vue {
  @Prop({
    type: String,
    default: POSITIONS.BOTTOM_LEFT
  })
  private displayPosition!: string;

  @Prop({
    type: Boolean,
    default: true
  })
  private isFullWidth!: boolean;

  @Prop({
    type: String
  })
  private selector!: string;

  @Prop({
    type: MouseEvent
  })
  private currentEvent!: MouseEvent;

  @Prop({
    type: Number,
    default: 1,
  })
  private parentZoom!: number;

  @Prop({
    type: Number,
    default: 1,
  })
  private zoom!: number;

  private dropDownLayer: HTMLBodyElement = <HTMLBodyElement> document.body;
  private isMobile = window.innerWidth >= 320 && window.innerWidth <= 940;
  private realZoom = this.isMobile ? this.zoom * BODY_ZOOM : 1;
  private realParentZoom = this.isMobile ? this.parentZoom : 1;
  private styleObject: styleObject = {
    width: null,
    top: null,
    left: null,
    right: null,
    bottom: null,
    position: 'absolute'
  };
  // @ts-ignore
  private resizeObserver: ResizeObserver = new ResizeObserver(this.$_abstractDropdownContent_calculatePosition);

  private mounted() {
    this.resizeObserver.observe(this.$el);

    this.dropDownLayer.appendChild(this.$el);

    this.$_abstractDropdownContent_calculatePosition();
  }

  private beforeDestroy() {
    this.resizeObserver.unobserve(this.$el);

    if (this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  }

  private $_abstractDropdownContent_calculatePosition() {
    const parentElement = this.selector
      ? this.$parent.$el.querySelector(this.selector)
      : this.$parent.$el;

    if (parentElement) {
      let { bottom, left, right, top, width } = parentElement.getBoundingClientRect();
      const bodyZoom = this.isMobile ? BODY_ZOOM : 1;

      bottom /= this.realParentZoom;
      left /= this.realParentZoom;
      right /= this.realParentZoom;
      top /= this.realParentZoom;
      width /= this.realParentZoom * bodyZoom;

      this.styleObject.width = this.isFullWidth ? `${width}px` : null;

      switch (this.displayPosition) {
        case POSITIONS.BOTTOM_LEFT:
          this.$_abstractDropdownContent_displayBottomLeft(bottom, left);

          break;
        case POSITIONS.BOTTOM_RIGHT:
          this.$_abstractDropdownContent_displayBottomRight(bottom, right);

          break;
        case POSITIONS.TOP_LEFT:
          this.$_abstractDropdownContent_displayTopLeft(top, left);

          break;
        case POSITIONS.TOP_RIGHT:
          this.$_abstractDropdownContent_displayTopRight(top, right);

          break;
        case POSITIONS.TOP_CENTER:
          this.$_abstractDropdownContent_displayTopCenter(top, left, width);

          break;
        case POSITIONS.BOTTOM_CENTER:
          this.$_abstractDropdownContent_displayBottomCenter(bottom, left, width);

          break;
        case POSITIONS.CURSOR:
          this.$_abstractDropdownContent_displayOnCursor();

          break;
        case POSITIONS.SCREEN_CENTER:
          this.$_abstractDropdownContent_displayOnScreenCenter();

          break;
        default:
          this.$_abstractDropdownContent_displayBottomLeft(bottom, left);
      }
    }
  }

  private $_abstractDropdownContent_displayBottomLeft(bottom: number, left: number): void {
    this.styleObject.top = this.$_abstractDropdownContent_getTopByParentBottom(bottom);
    this.styleObject.left = this.$_abstractDropdownContent_getLeftByParentLeft(left);
    this.styleObject.bottom = null;
    this.styleObject.right = null;
  }

  private $_abstractDropdownContent_displayBottomRight(bottom: number, right: number): void {
    this.styleObject.top = this.$_abstractDropdownContent_getTopByParentBottom(bottom);
    this.styleObject.right = this.$_abstractDropdownContent_getRightByParentRight(right);
    this.styleObject.bottom = null;
    this.styleObject.left = null;
  }

  private $_abstractDropdownContent_displayTopLeft(top: number, left: number): void {
    this.styleObject.bottom = this.$_abstractDropdownContent_getBottomByParentTop(top);
    this.styleObject.left = this.$_abstractDropdownContent_getLeftByParentLeft(left);
    this.styleObject.top = null;
    this.styleObject.right = null;
  }

  private $_abstractDropdownContent_displayTopCenter(top: number, left: number, width: number): void {
    this.styleObject.bottom = this.$_abstractDropdownContent_getBottomByParentTop(top);
    this.styleObject.left = this.$_abstractDropdownContent_getLeftByParentLeftAndWidth(left, width);
    this.styleObject.top = null;
    this.styleObject.right = null;
  }

  private $_abstractDropdownContent_displayBottomCenter(bottom: number, left: number, width: number): void {
    this.styleObject.top = this.$_abstractDropdownContent_getTopByParentBottom(bottom);
    this.styleObject.left = this.$_abstractDropdownContent_getLeftByParentLeftAndWidth(left, width);
    this.styleObject.bottom = null;
    this.styleObject.right = null;
  }

  private $_abstractDropdownContent_displayTopRight(top: number, right: number): void {
    this.styleObject.bottom = this.$_abstractDropdownContent_getBottomByParentTop(top);
    this.styleObject.right = this.$_abstractDropdownContent_getRightByParentRight(right);
    this.styleObject.top = null;
    this.styleObject.left = null;
  }

  private $_abstractDropdownContent_displayOnCursor(): void {
    this.styleObject.top = `${this.currentEvent.pageY}px`;
    this.styleObject.left = `${this.currentEvent.pageX}px`;
    this.styleObject.bottom = null;
    this.styleObject.right = null;
  }

  private $_abstractDropdownContent_displayOnScreenCenter(): void {
    const element: HTMLElement = <HTMLElement> this.$el;
    const elementHeight = element.offsetHeight * this.realZoom;
    const elementWidth = element.offsetWidth * this.realZoom;
    const top = (window.innerHeight - elementHeight) / (2 * this.realZoom);
    const left = (window.innerWidth - elementWidth) / (2 * this.realZoom);

    this.styleObject.top = `${Math.max(top, 0)}px`;
    this.styleObject.left = `${Math.max(left, 0)}px`;
    this.styleObject.bottom = null;
    this.styleObject.right = null;
  }

  private $_abstractDropdownContent_getLeftByParentLeft(parentLeft: number): string {
    const element: HTMLElement = <HTMLElement> this.$el;
    const windowWidth = window.innerWidth;
    const elementWidth = element.offsetWidth * this.realZoom;
    const left = windowWidth - parentLeft >= elementWidth
      ? parentLeft
      : Math.max(windowWidth - elementWidth, 0);

    return `${left / this.realZoom}px`;
  }

  private $_abstractDropdownContent_getLeftByParentLeftAndWidth(parentLeft: number, parentWidth: number): string {
    const element: HTMLElement = <HTMLElement> this.$el;
    const windowWidth = window.innerWidth;
    const elementWidth = element.offsetWidth * this.realZoom;
    const left = windowWidth - parentLeft >= elementWidth
      ? Math.max(parentLeft + (parentWidth - elementWidth) / 2, 0)
      : Math.max(windowWidth - elementWidth, 0);

    return `${left / this.realZoom}px`;
  }

  private $_abstractDropdownContent_getRightByParentRight(parentRight: number): string {
    const element: HTMLElement = <HTMLElement> this.$el;
    const windowWidth = window.innerWidth;
    const elementWidth = element.offsetWidth * this.realZoom;
    const right = parentRight >= elementWidth
      ? Math.max(windowWidth - parentRight, 0)
      : Math.max(windowWidth - elementWidth, 0);

    return `${right / this.realZoom}px`;
  }

  private $_abstractDropdownContent_getTopByParentBottom(parentBottom: number): string {
    const element: HTMLElement = <HTMLElement> this.$el;
    const windowHeight = window.innerHeight;
    const elementHeight = element.offsetHeight * this.realZoom;
    const top = windowHeight - parentBottom >= elementHeight
      ? parentBottom
      : Math.max(windowHeight - elementHeight, 0);

    return `${top / this.realZoom}px`;
  }

  private $_abstractDropdownContent_getBottomByParentTop(parentTop: number): string {
    const element: HTMLElement = <HTMLElement> this.$el;
    const windowHeight = window.innerHeight;
    const elementHeight = element.offsetHeight * this.realZoom;
    const top = parentTop >= elementHeight
      ? Math.max(windowHeight - parentTop, 0)
      : Math.max(windowHeight - elementHeight, 0);

    return `${top / this.realZoom}px`;
  }
}
